import { getAuth } from 'firebase/auth';

const VOICE_PROXY_URL = 'wss://voice-proxy-704989543070.europe-west1.run.app/session';

export type VoiceChatState = 'idle' | 'connecting' | 'connected' | 'disconnecting';
export type VoicePhase = 'listening' | 'thinking' | 'speaking';

export interface VoiceTranscriptEvent {
  sender: 'user' | 'agent';
  text: string;
}

export interface VoiceCard {
  type: string;
  data?: any;
  actions?: any[];
}

interface Listeners {
  onState?: (s: VoiceChatState) => void;
  onPhase?: (p: VoicePhase) => void;
  onTranscript?: (e: VoiceTranscriptEvent) => void;
  onError?: (msg: string) => void;
  onSessionInfo?: (info: { tier: string; remainingSeconds: number; threadId?: string }) => void;
  onCard?: (cards: VoiceCard[], skillName?: string) => void;
  onTimeWarning?: (secondsLeft: number) => void;
  onQuotaExhausted?: (reason: string) => void;
}

const QUOTA_REASON_MARKERS = ['exhausted', 'limit', 'not configured'];

// Map server-emitted reason codes (and synthetic transport errors) to
// user-facing strings. Server-side codes are kept internal for log/debug;
// users never see model names or internal identifiers.
const REASON_MAP: Record<string, string> = {
  // Voice-proxy explicit codes (cloud-run-voice-proxy/server.js)
  gemini_disconnected: 'f8 is unavailable right now — please try again in a moment.',
  time_limit: 'session ended — time limit reached.',
  user_ended: 'voice chat ended.',
  client_disconnected: 'voice chat ended.',
  error: 'voice chat ended unexpectedly — please try again.',
  // Auth-flavoured codes (defensive — server may emit these in future)
  unauthorized: 'sign-in expired — please sign in again.',
  unauthenticated: 'sign-in expired — please sign in again.',
};

const FRIENDLY_FALLBACK = 'voice chat ended unexpectedly — please try again.';

export function mapReasonToUserMessage(reason: string | null | undefined): string {
  if (!reason) return FRIENDLY_FALLBACK;
  const direct = REASON_MAP[reason];
  if (direct) return direct;
  // If the reason looks like an internal code (snake_case, no spaces, short),
  // fall back to a generic message. Real prose has spaces. This catches
  // unmapped future codes and any model/internal identifier that leaks.
  const looksLikeCode = !reason.includes(' ') && reason.length < 60;
  if (looksLikeCode) return FRIENDLY_FALLBACK;
  return reason;
}

class VoiceChatService {
  private ws: WebSocket | null = null;
  private state: VoiceChatState = 'idle';
  private audioCtx: AudioContext | null = null;
  private playbackCtx: AudioContext | null = null;
  private micStream: MediaStream | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private micSource: MediaStreamAudioSourceNode | null = null;
  private playbackQueue: Float32Array[] = [];
  private isPlaying = false;
  private hasPendingAudio = false;
  private muted = false;
  private playbackMuted = false;
  private listeners: Listeners = {};
  private currentSource: AudioBufferSourceNode | null = null;
  private chunkBuffer: string[] = [];
  private lastErrorReason: string | null = null;
  /**
   * The chat thread this session's transcript and skill turns are written to
   * (PPL-534). Either the thread the caller asked to resume, or the one the
   * proxy created and reported back in `session_start`. Kept after the socket
   * closes so the UI can reopen that thread once the call ends; cleared when
   * the next session connects.
   */
  private sessionThreadId: string | null = null;

  on(listeners: Listeners) {
    this.listeners = { ...this.listeners, ...listeners };
  }

  getState(): VoiceChatState {
    return this.state;
  }

  isMuted(): boolean {
    return this.muted;
  }

  /** The thread this session wrote to — see `sessionThreadId`. */
  getThreadId(): string | null {
    return this.sessionThreadId;
  }

  private setState(s: VoiceChatState) {
    this.state = s;
    this.listeners.onState?.(s);
  }

  private updatePhase() {
    let phase: VoicePhase;
    if (this.isPlaying) phase = 'speaking';
    else if (this.hasPendingAudio) phase = 'thinking';
    else phase = 'listening';
    this.listeners.onPhase?.(phase);
  }

  private async getAuthToken(): Promise<string> {
    const user = getAuth().currentUser;
    if (!user) throw new Error('Not signed in');
    return user.getIdToken();
  }

  /**
   * `threadId` resumes an existing f8 chat thread (PPL-534). Without it the
   * proxy creates a fresh "Voice Chat" thread per session, so the transcript —
   * and every skill card, a draft included — was persisted into a thread the
   * caller was not looking at.
   */
  async connect({ threadId }: { threadId?: string | null } = {}): Promise<void> {
    if (this.state !== 'idle') return;
    this.setState('connecting');
    this.lastErrorReason = null;
    this.sessionThreadId = threadId || null;

    try {
      const token = await this.getAuthToken();

      this.audioCtx = new AudioContext({ sampleRate: 16000 });
      this.playbackCtx = new AudioContext({ sampleRate: 24000 });
      // Resume in case browser created them suspended (autoplay policy).
      if (this.audioCtx.state === 'suspended') await this.audioCtx.resume();
      if (this.playbackCtx.state === 'suspended') await this.playbackCtx.resume();

      this.micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      await this.audioCtx.audioWorklet.addModule('/audio-worklets/pcm-encoder.js');
      this.workletNode = new AudioWorkletNode(this.audioCtx, 'pcm-encoder');
      this.micSource = this.audioCtx.createMediaStreamSource(this.micStream);
      this.micSource.connect(this.workletNode);

      this.workletNode.port.onmessage = (ev) => {
        if (this.muted || this.playbackMuted) return;
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
        const bytes = new Uint8Array(ev.data as ArrayBuffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        const base64 = btoa(binary);
        this.ws.send(JSON.stringify({ type: 'audio', data: base64 }));
      };

      let wsUrl = `${VOICE_PROXY_URL}?token=${encodeURIComponent(token)}&platform=web`;
      if (threadId) wsUrl += `&threadId=${encodeURIComponent(threadId)}`;
      this.ws = new WebSocket(wsUrl);

      await new Promise<void>((resolve, reject) => {
        if (!this.ws) return reject(new Error('No WS'));
        const timeout = setTimeout(() => {
          reject(new Error('WebSocket open timed out after 10s'));
        }, 10000);
        this.ws.onopen = () => {
          clearTimeout(timeout);
          this.setState('connected');
          this.updatePhase();
          resolve();
        };
        this.ws.onerror = () => {
          clearTimeout(timeout);
          reject(new Error('WebSocket error'));
        };
        this.ws.onmessage = (ev) => this.handleMessage(ev);
        this.ws.onclose = (ev) => {
          clearTimeout(timeout);
          if (this.state === 'connecting') {
            const rawReason = this.lastErrorReason || ev.reason || `Connection closed (code ${ev.code})`;
            // Quota path stays raw — the launcher card and quota UI render
            // friendly text from data.reason on the cloud-functions response.
            if (this.isQuotaReason(rawReason)) {
              this.listeners.onQuotaExhausted?.(rawReason);
            }
            // Keep the raw reason in the thrown Error for console/debug,
            // but never pass internal codes to the UI.
            console.error('[voiceChat] connect close raw reason:', rawReason);
            reject(new Error(mapReasonToUserMessage(rawReason)));
          } else {
            this.handleClose();
          }
        };
      });
    } catch (err) {
      console.error('[voiceChat] connect error:', err);
      await this.cleanup();
      this.setState('idle');
      throw err;
    }
  }

  private isQuotaReason(reason: string): boolean {
    if (!reason) return false;
    const lower = reason.toLowerCase();
    return QUOTA_REASON_MARKERS.some((m) => lower.includes(m));
  }

  private handleMessage(ev: MessageEvent) {
    if (typeof ev.data !== 'string') return;
    let msg: any;
    try {
      msg = JSON.parse(ev.data);
    } catch {
      return;
    }

    switch (msg.type) {
      case 'session_start':
        if (msg.threadId) this.sessionThreadId = msg.threadId;
        this.listeners.onSessionInfo?.({
          tier: msg.tier || 'free',
          remainingSeconds: msg.remainingSeconds || 0,
          threadId: msg.threadId,
        });
        break;
      case 'audio':
        if (msg.data) {
          this.chunkBuffer.push(msg.data);
          if (!this.isPlaying && !this.hasPendingAudio) {
            this.hasPendingAudio = true;
            this.updatePhase();
          }
        }
        break;
      case 'turn_complete':
        this.flushChunkBuffer();
        break;
      case 'transcript':
        this.listeners.onTranscript?.({ sender: msg.sender, text: msg.text });
        // If user transcript arrives, surface 'thinking' even before audio.
        if (msg.sender === 'user' && !this.isPlaying && !this.hasPendingAudio) {
          this.hasPendingAudio = true;
          this.updatePhase();
        }
        break;
      case 'card':
        if (Array.isArray(msg.cards)) {
          this.listeners.onCard?.(msg.cards as VoiceCard[], msg.skillName);
        }
        break;
      case 'interrupted':
        this.clearPlayback();
        break;
      case 'time_warning':
        this.listeners.onTimeWarning?.(msg.remainingSeconds || 60);
        break;
      case 'error':
        this.lastErrorReason = msg.reason || 'Session error';
        if (this.isQuotaReason(this.lastErrorReason || '')) {
          // Quota messages from cloud-functions are crafted user-facing
          // strings — pass through.
          this.listeners.onQuotaExhausted?.(this.lastErrorReason || '');
        } else {
          console.error('[voiceChat] error msg raw reason:', this.lastErrorReason);
          this.listeners.onError?.(mapReasonToUserMessage(this.lastErrorReason));
        }
        break;
      case 'session_end':
        console.error('[voiceChat] session_end raw reason:', msg.reason);
        this.listeners.onError?.(mapReasonToUserMessage(msg.reason));
        break;
    }
  }

  private flushChunkBuffer() {
    if (this.chunkBuffer.length === 0) return;
    const chunks = this.chunkBuffer;
    this.chunkBuffer = [];

    // Decode each base64 chunk separately, then concatenate the raw bytes.
    // Joining base64 strings only works if every chunk is a 3-byte multiple,
    // which Gemini's audio chunks are not.
    let totalLen = 0;
    const decoded: string[] = [];
    for (const b64 of chunks) {
      try {
        const bin = atob(b64);
        decoded.push(bin);
        totalLen += bin.length;
      } catch (err) {
        console.warn('[voiceChat] skipping malformed audio chunk', err);
      }
    }
    if (totalLen === 0) return;

    const bytes = new Uint8Array(totalLen);
    let offset = 0;
    for (const bin of decoded) {
      for (let i = 0; i < bin.length; i++) {
        bytes[offset + i] = bin.charCodeAt(i);
      }
      offset += bin.length;
    }

    const sampleCount = bytes.length >> 1;
    const float32 = new Float32Array(sampleCount);
    for (let i = 0; i < sampleCount; i++) {
      let v = bytes[i * 2] | (bytes[i * 2 + 1] << 8);
      if (v >= 0x8000) v -= 0x10000;
      float32[i] = v / 0x8000;
    }

    this.playbackQueue.push(float32);
    this.playbackMuted = true;
    if (!this.isPlaying) this.playNext();
  }

  private playNext() {
    if (!this.playbackCtx || this.playbackQueue.length === 0) {
      this.isPlaying = false;
      this.hasPendingAudio = false;
      this.playbackMuted = false;
      this.updatePhase();
      return;
    }
    this.isPlaying = true;
    this.hasPendingAudio = false;
    this.updatePhase();

    const samples = this.playbackQueue.shift()!;
    const buffer = this.playbackCtx.createBuffer(1, samples.length, this.playbackCtx.sampleRate);
    buffer.getChannelData(0).set(samples);
    const src = this.playbackCtx.createBufferSource();
    src.buffer = buffer;
    src.connect(this.playbackCtx.destination);
    src.onended = () => {
      this.currentSource = null;
      this.playNext();
    };
    this.currentSource = src;
    src.start();
  }

  private clearPlayback() {
    this.playbackQueue = [];
    this.chunkBuffer = [];
    if (this.currentSource) {
      try { this.currentSource.stop(); } catch {}
      this.currentSource = null;
    }
    this.isPlaying = false;
    this.hasPendingAudio = false;
    this.playbackMuted = false;
    this.updatePhase();
  }

  private handleClose() {
    if (this.state !== 'idle') {
      this.cleanup().then(() => this.setState('idle'));
    }
  }

  setMuted(muted: boolean) {
    this.muted = muted;
  }

  sendContextUpdate(data: any) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({ type: 'context', data }));
  }

  async disconnect(): Promise<void> {
    if (this.state === 'idle' || this.state === 'disconnecting') return;
    this.setState('disconnecting');
    try {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'end_session' }));
        this.ws.close(1000, 'User ended');
      }
    } catch {}
    await this.cleanup();
    this.setState('idle');
  }

  private async cleanup() {
    this.clearPlayback();
    this.muted = false;
    this.playbackMuted = false;

    try { this.workletNode?.disconnect(); } catch {}
    try { this.micSource?.disconnect(); } catch {}
    this.workletNode = null;
    this.micSource = null;

    if (this.micStream) {
      this.micStream.getTracks().forEach((t) => t.stop());
      this.micStream = null;
    }

    if (this.audioCtx) {
      try { await this.audioCtx.close(); } catch {}
      this.audioCtx = null;
    }
    if (this.playbackCtx) {
      try { await this.playbackCtx.close(); } catch {}
      this.playbackCtx = null;
    }

    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;
      this.ws = null;
    }
  }
}

const voiceChat = new VoiceChatService();
export default voiceChat;
